import { useCallback, useEffect, useRef, useState } from "react";

import {
	getUserIdsWithLinkedinConversation,
	LinkedinSenderStates,
	NO_SENDER_STATES,
	senderStatesForFailedCheck,
	senderStatesFromCheck,
} from "./filterLinkedinSenders.util";

import type { Conversation } from "@/pages/Private/pages/Apollo/schema/get-people";
import type {
	CheckConnectionQuery,
	CheckConnectionResponse,
} from "@/pages/Private/pages/Users/schema/get-users";
import type { User } from "@/pages/Public/pages/Login/schema/login";

/** The slice of a lead the check reads. */
export interface ConnectionCheckLead {
	id: number;
	prospect?: { linkedinUrl?: string | null; conversations?: Conversation[] | null } | null;
}

/** The RTK lazy trigger, narrowed to what the hook calls. */
export type CheckConnectionTrigger = (query: CheckConnectionQuery) => {
	unwrap: () => Promise<CheckConnectionResponse>;
};

interface UseLinkedinConnectionCheckParams {
	lead: ConnectionCheckLead | undefined;
	sendingUsers: User[] | undefined;
	checkConnection: CheckConnectionTrigger;
}

export interface LinkedinConnectionCheck {
	connectedUserIds: Set<number>;
	/** Per sender: who can message, whose invitation is pending, who is unknown (PREC-4216). */
	senderStates: LinkedinSenderStates;
	connectionCheckDone: boolean;
	/** Asks again for the current lead, e.g. after the API refused a send in a state we did not see. */
	recheck: () => void;
}

/** What one finished check answered, and for which lead. */
interface ConnectionCheckResult {
	leadId: number | null;
	states: LinkedinSenderStates;
}

const NO_RESULT: ConnectionCheckResult = { leadId: null, states: NO_SENDER_STATES };

/**
 * What each of the company's LinkedIn senders can do with the current lead (PREC-4216):
 * `users/check-connection` answers each sender's state for this lead (connected, a chat, its own
 * invitation pending, none), and a chat the loaded thread shows counts as a chat too — a chat
 * that exists reaches its other side whatever the connection table says (PREC-4221). An
 * invitation-only conversation is not a chat, and a failed check reads every sender as unknown,
 * never as connected (fail closed).
 *
 * Two things keep the answer honest across lead switches (PREC-4222). The check is a request,
 * and a request for the previous lead can answer *after* the current lead's — so every call
 * takes a sequence number and an answer to a superseded call is dropped. And "done" is not a
 * flag flipped by the effect but a comparison: the stored answer belongs to the current lead.
 * That closes the render between a lead switch and the effect that starts its check, in
 * which the previous lead's answer used to read as this lead's — the sender options built in
 * that render labelled an already-chatting sender "will send invite", and a draft that
 * captured the label kept it.
 */
export const useLinkedinConnectionCheck = ({
	lead,
	sendingUsers,
	checkConnection,
}: UseLinkedinConnectionCheckParams): LinkedinConnectionCheck => {
	const [result, setResult] = useState<ConnectionCheckResult>(NO_RESULT);
	const [recheckCount, setRecheckCount] = useState(0);
	const recheck = useCallback(() => setRecheckCount((count) => count + 1), []);
	const requestSeq = useRef(0);
	const leadId = lead?.id ?? null;

	useEffect(() => {
		const seq = ++requestSeq.current;
		const settle = (states: LinkedinSenderStates) => {
			// A later call owns the state now — this answer is for a lead that is gone.
			if (seq === requestSeq.current) {
				setResult({ leadId, states });
			}
		};

		// Senders not loaded yet: nothing is known, so nothing is answered. Settling "nobody is
		// connected" here read as done, and the composer's default decided on it before the real
		// check could answer (PREC-4312).
		if (!sendingUsers) {
			return;
		}

		const linkedinUrl = lead?.prospect?.linkedinUrl;
		const linkedinUserIds = sendingUsers.filter((u) => u.unipile?.isLoggedIn).map((u) => u.id);

		if (!linkedinUrl || linkedinUserIds.length === 0) {
			// Nothing to ask: no profile to check, or no sender with a LinkedIn session.
			// eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
			setResult((prev) =>
				prev.leadId === leadId && prev.states === NO_SENDER_STATES
					? prev
					: { leadId, states: NO_SENDER_STATES }
			);

			return;
		}

		// Users who already hold a LinkedIn chat with this lead (never an invitation-only row).
		const usersWithChat = getUserIdsWithLinkedinConversation(
			lead?.prospect?.conversations ?? [],
			lead?.id ?? 0
		);

		checkConnection({ userIds: linkedinUserIds, linkedinUrl, listPersonId: lead?.id })
			.unwrap()
			.then((response) => settle(senderStatesFromCheck(response.results, usersWithChat)))
			// Fail closed: a failed check is not "connected" and not "no invitation" (PREC-4216).
			.catch(() => settle(senderStatesForFailedCheck(linkedinUserIds)));
	}, [lead, leadId, sendingUsers, checkConnection, recheckCount]);

	// Until this lead's check answers, the stored states are another lead's: nothing is known about
	// this one, so neither its notice nor its deadline may show (PREC-4216).
	const isCurrent = result.leadId === leadId;
	const states = isCurrent ? result.states : NO_SENDER_STATES;

	return {
		connectedUserIds: states.connectedUserIds,
		senderStates: states,
		connectionCheckDone: isCurrent,
		recheck,
	};
};
